import {
  Button,
  Input,
  Label,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast
} from "@veritrellis/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.js";
import { FormField } from "../components/FormField.js";
import { apiFetch } from "../lib/api.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";

type NotificationChannel = "email" | "sms" | "whatsapp" | "voice";

type PreferenceCell = {
  event_type: string;
  channel: NotificationChannel;
  enabled: boolean;
};

type NotificationPreferencesResponse = {
  phone: string | null;
  preferences: PreferenceCell[];
};

const channelColumns: { channel: NotificationChannel; label: string }[] = [
  { channel: "email", label: "Email" },
  { channel: "sms", label: "SMS" },
  { channel: "whatsapp", label: "WhatsApp" },
  { channel: "voice", label: "Voice" }
];

const eventLabels: Record<string, string> = {
  "membership.role_changed": "Your role changed",
  "approval.pending": "Approval needed from you",
  "approval.decided": "Decision on your request",
  "quota.threshold": "Usage limit alerts"
};

const cellKey = (eventType: string, channel: NotificationChannel) => `${eventType}:${channel}`;

const orderedEventTypes = (preferences: PreferenceCell[]) => {
  const seen: string[] = [];
  for (const cell of preferences) {
    if (!seen.includes(cell.event_type)) {
      seen.push(cell.event_type);
    }
  }
  return seen;
};

export const SettingsNotificationsPage = () => {
  const queryClient = useQueryClient();
  const { workspaceId, isLoading } = useWorkspaceId();
  const [phone, setPhone] = useState("");
  const [matrix, setMatrix] = useState<Record<string, boolean>>({});

  const q = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: async () => {
      const res = await apiFetch("/admin/me/notification-preferences");
      if (!res.ok) {
        throw new Error("notification_preferences_failed");
      }
      return (await res.json()) as NotificationPreferencesResponse;
    }
  });

  useEffect(() => {
    if (q.data === undefined) {
      return;
    }
    setPhone(q.data.phone ?? "");
    setMatrix(
      q.data.preferences.reduce<Record<string, boolean>>((acc, cell) => {
        acc[cellKey(cell.event_type, cell.channel)] = cell.enabled;
        return acc;
      }, {})
    );
  }, [q.data]);

  const save = useMutation({
    mutationFn: async () => {
      const preferences: PreferenceCell[] = (q.data?.preferences ?? []).map((cell) => ({
        event_type: cell.event_type,
        channel: cell.channel,
        enabled: matrix[cellKey(cell.event_type, cell.channel)] ?? cell.enabled
      }));
      const trimmedPhone = phone.trim();
      const res = await apiFetch("/admin/me/notification-preferences", {
        method: "PUT",
        body: JSON.stringify({
          phone: trimmedPhone.length === 0 ? null : trimmedPhone,
          preferences
        })
      });
      if (!res.ok) {
        throw new Error(`save_${String(res.status)}`);
      }
      return (await res.json()) as NotificationPreferencesResponse;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["notification-preferences"], data);
      toast.show({
        title: "Saved",
        message: "Your notification preferences were updated.",
        color: "green"
      });
    },
    onError: (err: unknown) => {
      const code = err instanceof Error ? err.message : "";
      const msg =
        code === "save_400"
          ? "Check the phone number format (E.164, e.g. +14155552671)."
          : "Could not save notification preferences.";
      toast.show({ title: "Error", message: msg, color: "red" });
    }
  });

  if (isLoading) {
    return <p className="text-sm">Loading…</p>;
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }
  if (q.isLoading) {
    return <p className="text-sm">Loading notification preferences…</p>;
  }
  if (q.isError || q.data === undefined) {
    return <p className="text-sm text-destructive">Failed to load notification preferences.</p>;
  }

  const eventTypes = orderedEventTypes(q.data.preferences);

  return (
    <div className="admin-page-stack">
      <PageHeader
        title="Notifications"
        description="Choose how you are notified for events that involve you. These settings are personal and apply across every workspace you belong to."
      />

      <section className="admin-panel" aria-labelledby="notifications-phone-heading">
        <div className="admin-panel-header">
          <h2 id="notifications-phone-heading" className="admin-panel-title">
            Phone number
          </h2>
        </div>
        <div className="admin-panel-body-padded">
          <FormField>
            <Label htmlFor="notification-phone">Phone number</Label>
            <Input
              id="notification-phone"
              placeholder="+14155552671"
              value={phone}
              onChange={(e) => setPhone(e.currentTarget.value)}
            />
          </FormField>
          <p className="admin-settings-row-desc" style={{ margin: "0.75rem 0 0" }}>
            Use E.164 format (a leading <code className="admin-mono-inline">+</code> and country
            code). One number covers SMS, WhatsApp, and voice.
          </p>
        </div>
      </section>

      <section className="admin-panel" aria-labelledby="notifications-channels-heading">
        <div className="admin-panel-header">
          <h2 id="notifications-channels-heading" className="admin-panel-title">
            Channels per event
          </h2>
        </div>
        <div className="admin-panel-body">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                {channelColumns.map((col) => (
                  <TableHead key={col.channel}>{col.label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {eventTypes.map((eventType) => (
                <TableRow key={eventType}>
                  <TableCell>
                    <span className="text-sm font-medium">
                      {eventLabels[eventType] ?? eventType}
                    </span>
                  </TableCell>
                  {channelColumns.map((col) => {
                    const key = cellKey(eventType, col.channel);
                    return (
                      <TableCell key={col.channel}>
                        <Switch
                          checked={matrix[key] ?? false}
                          onCheckedChange={(checked) =>
                            setMatrix((prev) => ({ ...prev, [key]: checked }))
                          }
                          aria-label={`${eventLabels[eventType] ?? eventType} via ${col.label}`}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <p className="text-sm text-muted-foreground">
        SMS, WhatsApp, and voice require a phone number above and are sent only after an operator
        configures those channels. Until then, those toggles are saved but no message is sent.
      </p>

      <div>
        <Button disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save preferences"}
        </Button>
      </div>
    </div>
  );
};
